import React, { useState, useEffect } from 'react';
import { 
  Trophy, 
  Medal, 
  Target, 
  TrendingUp, 
  DollarSign, 
  Percent,
  Award,
  Star,
  Loader2,
  ChevronRight,
  BarChart3
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import api from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { useApp } from '../context/AppContext';
import logger from '../lib/logger';
import { PageShell } from '../components/layout/PageShell';
import { PageHero } from '../components/layout/PageHero';
import { getLightSectionTheme } from '../lib/sectionThemes';

const BADGE_STYLES = {
  gold: { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', icon: '🥇' },
  silver: { bg: 'bg-slate-100', text: 'text-slate-600', border: 'border-slate-300', icon: '🥈' },
  bronze: { bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', icon: '🥉' },
};

const AccuracyLeaderboardPage = () => {
  const { currency } = useApp();
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState({});
  const [myStats, setMyStats] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [leaderboardRes, myStatsRes] = await Promise.all([
        api.get('/accuracy/leaderboard'),
        api.get('/accuracy/my-stats')
      ]);
      
      setLeaderboard(leaderboardRes.data.leaderboard || []);
      setStats(leaderboardRes.data.stats || {});
      setMyStats(myStatsRes.data);
    } catch (error) {
      logger.error('Failed to load leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 95) return 'text-emerald-600';
    if (accuracy >= 85) return 'text-teal-600';
    if (accuracy >= 75) return 'text-blue-600';
    if (accuracy >= 50) return 'text-amber-600';
    return 'text-rose-500';
  };

  const getProgressColor = (accuracy) => {
    if (accuracy >= 95) return 'bg-emerald-500';
    if (accuracy >= 85) return 'bg-teal-500';
    if (accuracy >= 75) return 'bg-blue-500';
    if (accuracy >= 50) return 'bg-amber-500';
    return 'bg-rose-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
      </div>
    );
  }

  const __pageTheme = getLightSectionTheme('Navy');
  // Mirror the Comparison Analytics hero shape: full PageHero with
  // icon + 4 KPI stats. Badge counts are derived from the same
  // leaderboard payload that drives the table — no extra fetch.
  const goldCount = leaderboard.filter(e => e.badge === 'gold').length;
  const silverCount = leaderboard.filter(e => e.badge === 'silver').length;
  const bronzeCount = leaderboard.filter(e => e.badge === 'bronze').length;
  const avgAccuracy = leaderboard.length
    ? leaderboard.reduce((s, e) => s + (e.overall_accuracy || 0), 0) / leaderboard.length
    : 0;
  const accuracyHeroStats = [
    { label: 'Total Projections', value: leaderboard.length, testId: 'accuracy-stat-total' },
    { label: 'Avg Accuracy', value: `${avgAccuracy.toFixed(0)}%`, testId: 'accuracy-stat-avg' },
    { label: 'Gold Badges', value: goldCount, testId: 'accuracy-stat-gold' },
    { label: 'Silver / Bronze', value: `${silverCount} / ${bronzeCount}`, testId: 'accuracy-stat-other-badges' },
  ];
  return (
    <PageShell theme={__pageTheme} hero={(
      <PageHero
        title="Accuracy Leaderboard"
        description="Who's projecting impact most accurately, and why."
        theme={__pageTheme}
        icon={Trophy}
        stats={accuracyHeroStats}
        testId="accuracy-leaderboard-hero"
      />
    )}>
    <div className="space-y-6" data-testid="accuracy-leaderboard-page">
      {/* Header - matching standard style */}
      
      {/* Badge Guide */}
      <Card className="bg-slate-50">
        <CardContent className="py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Medal className="h-4 w-4" />
              <span className="font-medium">Badge Thresholds:</span>
            </div>
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-1">
                <span>🥇</span>
                <span className="text-yellow-700 font-medium">Gold: 95%+</span>
              </div>
              <div className="flex items-center gap-1">
                <span>🥈</span>
                <span className="text-slate-600 font-medium">Silver: 85-94%</span>
              </div>
              <div className="flex items-center gap-1">
                <span>🥉</span>
                <span className="text-orange-700 font-medium">Bronze: 75-84%</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Leaderboard */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-teal-600" />
            Top Projections
          </CardTitle>
          <CardDescription>
            Ranked by overall accuracy score
          </CardDescription>
        </CardHeader>
        <CardContent>
          {leaderboard.length === 0 ? (
            <div className="text-center py-12">
              <Trophy className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-600">No Projections Yet</h3>
              <p className="text-slate-500 mt-1 max-w-md mx-auto">
                Create projected scenarios using the "What-If" mode in Compare Scenarios, 
                then record accuracy when actual results come in.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {leaderboard.map((entry, index) => (
                <div 
                  key={entry.id}
                  className={`flex items-center gap-4 p-4 rounded-lg border ${
                    entry.badge ? BADGE_STYLES[entry.badge].bg : 'bg-white'
                  } ${entry.badge ? BADGE_STYLES[entry.badge].border : 'border-slate-200'}`}
                >
                  {/* Rank */}
                  <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center font-bold text-slate-600">
                    {index + 1}
                  </div>

                  {/* Scenario Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-slate-800 truncate">
                        {entry.projected_scenario_name}
                      </h4>
                      {entry.badge && (
                        <span className="text-lg">{BADGE_STYLES[entry.badge].icon}</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 truncate">
                      vs. {entry.actual_scenario_name}
                    </p>
                  </div>

                  {/* Accuracy Breakdown */}
                  <div className="hidden md:flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <p className="text-slate-400 text-xs">ROI</p>
                      <p className={`font-medium ${getAccuracyColor(entry.roi_accuracy)}`}>
                        {entry.roi_accuracy.toFixed(0)}%
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-slate-400 text-xs">KPI</p>
                      <p className={`font-medium ${getAccuracyColor(entry.kpi_accuracy)}`}>
                        {entry.kpi_accuracy.toFixed(0)}%
                      </p>
                    </div>
                    <div className="text-center">
                      <p className="text-slate-400 text-xs">Financial</p>
                      <p className={`font-medium ${getAccuracyColor(entry.financial_accuracy)}`}>
                        {entry.financial_accuracy.toFixed(0)}%
                      </p>
                    </div>
                  </div>

                  {/* Overall Score */}
                  <div className="text-right">
                    <p className={`text-xl font-bold ${getAccuracyColor(entry.overall_accuracy)}`}>
                      {entry.overall_accuracy.toFixed(1)}%
                    </p>
                    <div className="w-24 h-2 bg-slate-100 rounded-full mt-1 overflow-hidden">
                      <div 
                        className={`h-full ${getProgressColor(entry.overall_accuracy)} transition-all`}
                        style={{ width: `${entry.overall_accuracy}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* How It Works */}
      <Card className="border-dashed">
        <CardContent className="py-6">
          <h3 className="font-medium text-slate-800 mb-4 flex items-center gap-2">
            <Target className="h-5 w-5 text-purple-600" />
            How Accuracy Scoring Works
          </h3>
          <div className="grid md:grid-cols-3 gap-6 text-sm text-slate-600">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold">1</div>
                <span className="font-medium">Create Projection</span>
              </div>
              <p>Use "What-If" mode in Compare Scenarios to create projected scenarios with different LEI improvements.</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold">2</div>
                <span className="font-medium">Run Actual Analysis</span>
              </div>
              <p>When real data is available, run an actual analysis to get the true ROI, KPI uplift, and financial impact.</p>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 rounded-full bg-purple-100 text-purple-600 flex items-center justify-center text-xs font-bold">3</div>
                <span className="font-medium">Record Accuracy</span>
              </div>
              <p>Compare projected vs actual results to earn badges. Gold for 95%+ accuracy!</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </PageShell>
  );
};

export default AccuracyLeaderboardPage;
